import { CostingModel, CostingRowView } from 'shared/models';
import { liningNoCostingClause, uncostedLiningNumbers } from 'shared/core';

/** Shown in place of a figure a lining order does not carry (its price is on its Upper order). */
export const LINING_NO_FIGURE = '—';

export const GRAND_TOTAL_TOOLTIP = 'Lining orders are left out of Total Cost and Profit/Loss: their cost is counted in their Upper order. Order QTY and MFD QTY still include them.';

/** The part of a costing row the sandwich view reads. */
export type CostingRowSource = Pick<
  CostingModel,
  'IsLiningOrder' | 'IsUpperOrder' | 'UpperSaleOrderNumber' | 'LiningSaleOrderNumbers' | 'UncostedLiningSaleOrderNumbers' | 'IsLiningCostMissing'
>;

/**
 * Sandwich Lining display fields for one row. Pure: call it once when the list arrives and keep
 * the result on the row. Calling functions that return objects from the template for every row
 * freezes the page.
 */
export function costingRowView(row: CostingRowSource | null | undefined): CostingRowView {
  const isLining = row?.IsLiningOrder === true;
  const isSandwichUpper = !isLining && row?.IsUpperOrder === true;
  const isLiningCostMissing = isSandwichUpper && row?.IsLiningCostMissing === true;
  const upper = (row?.UpperSaleOrderNumber ?? '').trim();
  const linings = (row?.LiningSaleOrderNumbers ?? '').trim();

  const liningTooltip = upper ? `Priced on Upper order SO ${upper}` : 'Priced on its Upper order';
  const sandwichTooltip = linings
    ? `Includes the cost of lining order(s) SO ${linings}`
    : 'Includes the cost of its lining order(s)';
  // Only the linings with no costing are named; the others are in this order's cost.
  const uncosted = uncostedLiningNumbers(row?.UncostedLiningSaleOrderNumbers, row?.LiningSaleOrderNumbers);
  const missingTooltip = `${liningNoCostingClause(uncosted)}, so this order's cost leaves it out.`;

  let printNote = '';
  if (isLining) {
    printNote = upper ? `Lining, priced on SO ${upper}` : 'Lining';
  } else if (isSandwichUpper) {
    printNote = isLiningCostMissing ? 'Sandwich, lining not costed' : 'Sandwich';
  }

  return { isLining, isSandwichUpper, isLiningCostMissing, liningTooltip, sandwichTooltip, missingTooltip, printNote };
}

/** True when any row is a lining or a sandwich Upper, so the note about linings in the totals is worth showing. */
export function hasSandwichRows(rows: ReadonlyArray<Pick<CostingModel, 'RowView'>> | null | undefined): boolean {
  return (rows ?? []).some(r => r.RowView?.isLining === true || r.RowView?.isSandwichUpper === true);
}

/** Attaches the view to every row once; the rows are the objects the grid already holds. */
export function attachRowViews<T extends CostingModel>(rows: T[] | null | undefined): void {
  (rows ?? []).forEach(row => { row.RowView = costingRowView(row); });
}
